import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import VideogameAssetOutlinedIcon from "@mui/icons-material/VideogameAssetOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import CurrencyBitcoinOutlinedIcon from "@mui/icons-material/CurrencyBitcoinOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import TelegramIcon from "@mui/icons-material/Telegram";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { useLanguage } from "../i18n/LanguageContext";
import { TELEGRAM_HANDLE, TELEGRAM_URL } from "./SiteFooter";
import LangFlag from "./LangFlag";

const Sidebar = ({ open, onClose, onSearch }) => {
  const location = useLocation();
  const { t, lang, setLang } = useLanguage();
  const type = new URLSearchParams(location.search).get("type") || "";

  const sections = useMemo(
    () => [
      {
        label: t("sidebar.games"),
        links: [
          { label: t("sidebar.vip"), to: "/vip", icon: WorkspacePremiumOutlinedIcon },
          { label: t("sidebar.allGames"), to: "/", icon: ViewModuleOutlinedIcon },
          { label: t("sidebar.slots"), to: "/?type=slot", icon: VideogameAssetOutlinedIcon },
          { label: t("sidebar.liveCasino"), to: "/?type=casino", icon: VideocamOutlinedIcon },
          { label: t("sidebar.marsilya"), to: "/?type=original", icon: AutoAwesomeOutlinedIcon },
        ],
      },
      {
        label: t("sidebar.betting"),
        links: [
          { label: t("sidebar.sport"), to: "/spor", icon: SportsSoccerOutlinedIcon },
          { label: t("sidebar.crypto"), to: "/oyun/kripto", icon: CurrencyBitcoinOutlinedIcon },
        ],
      },
      {
        label: t("sidebar.account"),
        links: [
          {
            label: t("sidebar.chat"),
            key: "chat",
            icon: ForumOutlinedIcon,
            action: () => window.dispatchEvent(new Event("marsilya-open-chat")),
          },
          { label: t("sidebar.wallet"), to: "/cuzdan", icon: AccountBalanceWalletOutlinedIcon },
          { label: t("sidebar.contact"), to: "/iletisim", icon: HeadsetMicOutlinedIcon },
        ],
      },
    ],
    [t],
  );

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/" && !type;
    if (to.startsWith("/?type=")) return location.pathname === "/" && type === to.split("=")[1];
    return location.pathname === to;
  };

  const close = () => {
    onSearch?.("");
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1100px)").matches) {
      onClose();
    }
  };

  return (
    <>
      {open ? <div className="sidebar-backdrop" onClick={onClose} aria-hidden /> : null}
      <aside className={`sidebar sidebar-rail${open ? " open" : ""}`}>
        <nav className="sidebar-nav">
          {sections.map((section) => (
            <React.Fragment key={section.label}>
              <div className="sidebar-label">{section.label}</div>
              {section.links.map((item) => {
                const Icon = item.icon;
                const body = (
                  <>
                    <span className="sidebar-icon sidebar-icon-flat">
                      <Icon />
                    </span>
                    <span className="sidebar-text">{item.label}</span>
                  </>
                );
                if (item.action) {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="sidebar-link"
                      data-tip={item.label}
                      aria-label={item.label}
                      onClick={() => {
                        close();
                        item.action();
                      }}
                    >
                      {body}
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`sidebar-link${isActive(item.to) ? " active" : ""}`}
                    data-tip={item.label}
                    aria-label={item.label}
                    onClick={close}
                  >
                    {body}
                  </Link>
                );
              })}
            </React.Fragment>
          ))}
        </nav>

        <div className="sdb-foot">
          <div className="sdb-lang" role="group" aria-label={t("sidebar.language")}>
            {[
              { code: "tr", label: "Türkçe" },
              { code: "en", label: "English" },
            ].map((item) => (
              <button
                key={item.code}
                type="button"
                className={lang === item.code ? "on" : ""}
                data-tip={item.label}
                aria-label={item.label}
                onClick={() => setLang(item.code)}
              >
                <span className="sidebar-icon sidebar-icon-flat sidebar-icon-flag">
                  <LangFlag code={item.code} />
                </span>
                <span className="sdb-lang-text">{item.label}</span>
              </button>
            ))}
          </div>
          <a
            className="sdb-tg"
            href={TELEGRAM_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-tip={t("sidebar.telegram")}
            aria-label={t("sidebar.telegram")}
          >
            <span className="sidebar-icon sidebar-icon-flat sidebar-icon-tg">
              <TelegramIcon />
            </span>
            <span className="sdb-tg-copy">
              <b>{t("sidebar.telegram")}</b>
              <em>{TELEGRAM_HANDLE}</em>
            </span>
          </a>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
